import React, { useState, useEffect } from 'react';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { submitResponse } from '../utils/api';
import './SubmitPage.css';

const MAX_CHARS = 1000;

const ZIRVE_LOGO = 'https://musterideneyimizirvesi.com/images/zirve-logo.jpg';

function PageHeader() {
  return (
    <div className="submit-header">
      <img src={ZIRVE_LOGO} alt="Müşteri Deneyimi Zirvesi" className="zirve-logo-header" />
    </div>
  );
}

function PageFooter() {
  return (
    <footer className="submit-footer">
      <span>
        Powered by{' '}
        <a
          href="https://cloud4feed.com"
          target="_blank"
          rel="noopener noreferrer"
          className="c4f-link"
        >
          Cloud4Feed
        </a>{' '}
        · Yanıtlarınız anonimdir
      </span>
    </footer>
  );
}

export default function SubmitPage() {
  const [text, setText] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isListening, setIsListening] = useState(false);

  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
    isMicrophoneAvailable
  } = useSpeechRecognition();

  useEffect(() => {
    setIsListening(listening);
  }, [listening]);

  // Dinleme sırasında metin kutusunda gösterilecek canlı değer:
  // mevcut metin + o ana kadarki transcript (henüz kalıcı olarak işlenmedi)
  const displayValue = listening && transcript
    ? (text && !text.endsWith(' ') ? text + ' ' : text) + transcript
    : text;

  const toggleListening = () => {
    if (listening) {
      // Dinlemeyi durdur ve transcript'i metne BİR KEZ işle
      SpeechRecognition.stopListening();
      setText((prev) => {
        const separator = prev && !prev.endsWith(' ') ? ' ' : '';
        return (prev + separator + transcript).slice(0, MAX_CHARS);
      });
      resetTranscript();
    } else {
      resetTranscript();
      SpeechRecognition.startListening({ continuous: true, language: 'tr-TR' });
    }
  };

  const handleSubmit = async () => {
    // Dinleme açıkken gönderilirse, canlı transcript'i de dahil et
    const finalText = displayValue.trim().slice(0, MAX_CHARS);
    if (!finalText || finalText.length < 3) {
      setError('Lütfen en az birkaç kelime yazın.');
      return;
    }
    if (listening) {
      SpeechRecognition.stopListening();
      resetTranscript();
    }
    setText(finalText);
    setSubmitting(true);
    setError('');
    try {
      await submitResponse(finalText);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Gönderim sırasında bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="submit-page">
        <PageHeader />
        <div className="success-card">
          <div className="success-icon">✓</div>
          <h2>Teşekkürler!</h2>
          <p>Yanıtınız başarıyla alındı.<br />Katkınız için teşekkür ederiz.</p>
          <button className="btn-secondary" onClick={() => { setSubmitted(false); setText(''); }}>
            Yeni Yanıt Gönder
          </button>
        </div>
        <PageFooter />
      </div>
    );
  }

  return (
    <div className="submit-page">
      <PageHeader />

      <div className="submit-card">
        <h1 className="question-text">
          Bugün duyduğunuz en etkileyici cümle neydi?
        </h1>

        <div className="input-area">
          <textarea
            className={`response-textarea ${isListening ? 'listening' : ''}`}
            value={displayValue}
            onChange={(e) => setText(e.target.value.slice(0, MAX_CHARS))}
            placeholder="Zirve boyunca aklınızda kalan cümleleri paylaşın. Seçilen cümleler gün içinde sahnede ve etkinlik ekranlarında diğer katılımcılarla buluşsun."
            rows={6}
            disabled={submitting}
            readOnly={listening}
          />
          <div className="textarea-footer">
            <span className={`char-count ${displayValue.length > MAX_CHARS * 0.9 ? 'warn' : ''}`}>
              {displayValue.length} / {MAX_CHARS}
            </span>
            {browserSupportsSpeechRecognition && (
              <button
                className={`mic-button ${isListening ? 'active' : ''}`}
                onClick={toggleListening}
                disabled={submitting || !isMicrophoneAvailable}
                title={isListening ? 'Mikrofonu Durdur' : 'Sesli Giriş'}
              >
                {isListening && <span className="mic-pulse" />}
                <MicIcon />
                <span className="mic-label">{isListening ? 'Dinleniyor...' : 'Sesli Giriş'}</span>
              </button>
            )}
          </div>
        </div>

        {error && <div className="error-msg">{error}</div>}

        <button
          className="btn-submit"
          onClick={handleSubmit}
          disabled={submitting || !displayValue.trim() || displayValue.trim().length < 3}
        >
          {submitting ? <span className="spinner" /> : 'Gönder'}
        </button>
      </div>

      <PageFooter />
    </div>
  );
}

function MicIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.91-3c-.49 0-.9.36-.98.85C16.52 14.2 14.47 16 12 16s-4.52-1.8-4.93-4.15c-.08-.49-.49-.85-.98-.85-.61 0-1.09.54-1 1.14.49 3 2.89 5.35 5.91 5.78V20c0 .55.45 1 1 1s1-.45 1-1v-2.08c3.02-.43 5.42-2.78 5.91-5.78.1-.6-.39-1.14-1-1.14z"/>
    </svg>
  );
}
